/**
 * Role domain entity representing an organizational or store role
 *
 * Encapsulates role logic with validation and business rules.
 * Immutable by design to ensure data integrity.
 * Roles define a set of permissions that can be assigned to employees.
 *
 * @module domains/personnel/domain/entities/Role
 */

import type { Permission } from '@personnel/domain/types/PermissionsConst';
import type { RoleUuid, RoleConfiguration, CreateRoleInput, RoleApiData } from '@personnel/domain/types';
import type { RoleName } from '@personnel/domain/value-objects/RoleName';
import { createRoleName } from '@personnel/domain/value-objects/RoleName';
import { createOrganizationId } from '@personnel/domain/value-objects/OrganizationId';
import { MIN_PERMISSIONS_REQUIRED } from '@personnel/domain/value-objects/RolePermissions';

/**
 * Role entity representing an organizational or store role
 *
 * @example
 * const role: Role = {
 *   uuid: '550e8400-e29b-41d4-a716-446655440000',
 *   name: 'Manager',
 *   description: 'Store manager with full permissions',
 *   organizationUuid: '550e8400-e29b-41d4-a716-446655440001',
 *   roleConfiguration: { tipRetentionAmount: null, endSessionKitchen: false },
 *   permissions: [
 *     { frontendId: 'org-menu', backendIds: ['backoffice.access'], level: 'organization' },
 *     { frontendId: 'org-personnel', backendIds: ['admin.modifyEmployeesAndPermissions'], level: 'organization' },
 *   ],
 *   usersCount: 5,
 * };
 */
export type Role = Readonly<{
  uuid: RoleUuid;
  name: RoleName;
  description: string;
  organizationUuid: string;
  roleConfiguration: RoleConfiguration;
  permissions: readonly Permission[];
  usersCount: number;
}>;

/**
 * Factory: Creates a new role with validation
 *
 * Validates all input data using Value Objects.
 * Throws descriptive errors if validation fails.
 *
 * @param data - The input data for the new role
 * @returns {Omit<Role, 'uuid' | 'usersCount'>} A new role (without uuid, usersCount)
 * @throws {Error} When any validation fails
 *
 * @example
 * const newRole = createNewRole({
 *   name: 'Manager',
 *   description: 'Store manager with full access',
 *   organizationUuid: '550e8400-e29b-41d4-a716-446655440000',
 *   permissions: [
 *     { frontendId: 'org-menu', backendIds: ['backoffice.access'], level: 'organization' },
 *     { frontendId: 'org-personnel', backendIds: ['admin.modifyEmployeesAndPermissions'], level: 'organization' },
 *   ],
 *   roleConfiguration: { tipRetentionAmount: null, endSessionKitchen: false },
 * });
 */
export const createNewRole = (data: CreateRoleInput): Omit<Role, 'uuid' | 'usersCount'> => {
  // Validate using Value Objects - these throw on invalid input
  const name = createRoleName(data.name);
  const organizationUuid = createOrganizationId(data.organizationUuid);

  // Business Rule - At least one permission is required
  if (!data.permissions || data.permissions.length < MIN_PERMISSIONS_REQUIRED) {
    throw new Error('At least one permission is required');
  }

  const permissions = data.permissions;

  return Object.freeze({
    name,
    description: data.description?.trim() || '',
    organizationUuid,
    roleConfiguration: data.roleConfiguration ?? {
      tipRetentionAmount: null,
      endSessionKitchen: false,
    },
    permissions,
  });
};

/**
 * Factory: Reconstructs a role from API/storage data
 *
 * Used when loading role data from external sources.
 * Validates all data and converts to proper types.
 *
 * @param data - The raw data from API or storage
 * @returns {Role} A fully validated Role entity
 * @throws {Error} When any validation fails
 *
 * @example
 * const role = createRoleFromData(apiResponse.data);
 */
export const createRoleFromData = (data: RoleApiData): Role => {
  // Validate UUID first
  if (!data.uuid?.trim()) {
    throw new Error('Role UUID is required');
  }

  // Validate core fields
  const name = createRoleName(data.name);
  const organizationUuid = createOrganizationId(data.organizationUuid);

  // No permission minimum enforced when reconstructing from API data
  // The backend allows roles with zero permissions
  const permissions = data.permissions || [];

  return Object.freeze({
    uuid: data.uuid as RoleUuid,
    name,
    description: data.description?.trim() || '',
    organizationUuid,
    roleConfiguration: data.roleConfiguration,
    permissions,
    usersCount: data.usersCount ?? 0,
  });
};
